import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
// Import specific hooks needed instead of useAppService
import { useGetRagStatus, useStartIndexing, useContinueIndexing, useStopIndexing, useGetKnowledgeBaseStats } from '../services/appService'; 
import {
    Box,
    CircularProgress,
    Alert,
    Typography,
    Snackbar,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    IconButton,
    Divider,
    Button,
    TextField,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Tooltip,
    Paper,
    Grid,
    LinearProgress
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import io from 'socket.io-client';

// Helper to format bytes (can likely be removed if library handles it)
// const formatBytes = (bytes, decimals = 2) => { ... };

function KnowledgeBasePage() {
    const { appId } = useParams();
    const { token } = useAuth();
    // Directly use the imported hooks
    const getRagStatus = useGetRagStatus(); 
    const startIndexing = useStartIndexing();
    const continueIndexing = useContinueIndexing();
    const stopIndexing = useStopIndexing();
    const getKnowledgeBaseStats = useGetKnowledgeBaseStats();

    const [files, setFiles] = useState([]); // State to hold fetched files/folders
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPath, setCurrentPath] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const fileInputRef = useRef(null); // Ref for the hidden file input

    // State for Modals/Dialogs
    const [createFolderOpen, setCreateFolderOpen] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');

    // State for RAG
    const [ragStatus, setRagStatus] = useState({
        indexedDocuments: 0,
        totalChunks: 0,
        isIndexing: false,
        stopRequested: false,
        indexingStartTime: null,
        error: null,
        lastMessage: null
    });
    const [isActionLoading, setIsActionLoading] = useState(false);
    const [isStatusLoading, setIsStatusLoading] = useState(false);
    const statusIntervalRef = useRef(null); // Ref for interval timer

    // State for total files
    const [totalFilesCount, setTotalFilesCount] = useState(null);
    const [isLoadingTotalFiles, setIsLoadingTotalFiles] = useState(false);
    const [errorTotalFiles, setErrorTotalFiles] = useState(null);

    const [socket, setSocket] = useState(null);
    const [lastMessage, setLastMessage] = useState(null); // Store last progress message

    // const apiBaseUrl = `/api/apps/${appId}/knowledge-base`; // No longer needed directly

    // Function to fetch total file count (defined before useEffect uses it)
    const fetchTotalFiles = useCallback(async () => {
        if (!appId) return;
        setIsLoadingTotalFiles(true);
        setErrorTotalFiles(null);
        try {
            const stats = await getKnowledgeBaseStats(appId);
            setTotalFilesCount(stats.totalFiles);
        } catch (err) {
            console.error("Failed to fetch knowledge base stats:", err);
            setErrorTotalFiles(err.message || 'Failed to load total file count.');
            setTotalFilesCount(null); // Reset count on error
        } finally {
            setIsLoadingTotalFiles(false);
        }
    }, [appId, getKnowledgeBaseStats]);

    // --- API Call Handlers --- 

    // Fetch RAG Status
    const fetchRagStatus = useCallback(async (isInitial = false) => {
        if (!appId) return;
        if (isInitial) setIsStatusLoading(true); // Only show initial loading indicator
        console.log("[KnowledgeBasePage] Fetching RAG status...");
        try {
            const statusData = await getRagStatus(appId);
            console.log("[KnowledgeBasePage] RAG Status received:", statusData);
            setRagStatus(prev => ({
                ...prev,
                indexedDocuments: statusData.indexedDocuments ?? prev.indexedDocuments,
                totalChunks: statusData.totalChunks ?? prev.totalChunks,
                isIndexing: statusData.isIndexing ?? false,
                stopRequested: statusData.stopRequested ?? false,
                indexingStartTime: statusData.indexingStartTime || null,
                error: statusData.indexingError || null,
                lastMessage: statusData.lastMessage || prev.lastMessage
            }));

            if (!statusData.isIndexing) {
                if (statusIntervalRef.current) {
                    console.log("[KnowledgeBasePage] Indexing finished or stopped, clearing interval.");
                    clearInterval(statusIntervalRef.current);
                    statusIntervalRef.current = null;
                }
                if (isActionLoading) setIsActionLoading(false);
                if (!statusIntervalRef.current) {
                    statusIntervalRef.current = setInterval(() => fetchRagStatus(false), 15000);
                }
            } else {
                if (!statusIntervalRef.current || statusIntervalRef.current._interval !== 5000) {
                    console.log("[KnowledgeBasePage] Indexing detected, ensuring faster polling interval.");
                    if (statusIntervalRef.current) clearInterval(statusIntervalRef.current);
                    statusIntervalRef.current = setInterval(() => fetchRagStatus(false), 5000);
                }
                if (statusData.stopRequested) {
                    setIsActionLoading(true);
                } else {
                    setIsActionLoading(false);
                }
            }

        } catch (err) {
            console.error('[KnowledgeBasePage] Error fetching RAG status:', err);
            const errorMsg = err.message || 'Failed to load RAG status.';
            setRagStatus(prev => ({ ...prev, error: errorMsg }));
            if (statusIntervalRef.current) {
                console.log("[KnowledgeBasePage] Error fetching status, clearing interval.");
                clearInterval(statusIntervalRef.current);
                statusIntervalRef.current = null;
            }
            setIsActionLoading(false);
            setIsStatusLoading(false);
        } finally {
            if (isInitial) setIsStatusLoading(false);
        }
    }, [appId, getRagStatus, isActionLoading]);

    // Fetch files/folders for a given path
    const fetchFiles = useCallback(async (path) => {
        if (!appId) return;
        setLoading(true);
        setError(null);
        console.log(`[KnowledgeBasePage] Fetching files for path: ${path || '(root)'}`);
        try {
            // Use service hook (assuming one exists or is added)
            // Example: const filesData = await appService.useListKnowledgeBaseFiles()(appId, path);
            // Replace with actual axios/fetch call for now if service not ready
            const response = await fetch(`/api/apps/${appId}/knowledge-base/files?path=${encodeURIComponent(path)}`, {
                 headers: { Authorization: `Bearer ${token}` }
            });
            if (!response.ok) {
                 const errorData = await response.json().catch(() => ({ message: response.statusText }));
                 throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }
            const data = await response.json();

            // Sort: Folders first, then alphabetically
            const sortedData = data.sort((a, b) => {
                if (a.type === 'folder' && b.type !== 'folder') return -1;
                if (a.type !== 'folder' && b.type === 'folder') return 1;
                return a.name.localeCompare(b.name);
            });
            setFiles(sortedData);
            setCurrentPath(path);
        } catch (err) {
            console.error('[KnowledgeBasePage] Error fetching files:', err);
            const errorMsg = err.message || 'Failed to load files.';
            setError(errorMsg);
            setSnackbar({ open: true, message: errorMsg, severity: 'error' });
            // Basic back navigation on 404
            if (err.message.includes('404') && path !== '') {
                 const pathParts = path.split('/');
                 const parentPath = pathParts.slice(0, -1).join('/');
                 fetchFiles(parentPath); // Call fetchFiles directly
            }
        } finally {
            setLoading(false);
        }
    }, [appId, token]); // Simplified dependencies

    // Initial fetches and status polling setup
    useEffect(() => {
        fetchFiles(''); // Fetch root files
        fetchRagStatus(true); // Initial status fetch
        fetchTotalFiles(); // Fetch initial status and total file count when component mounts

        // Setup interval polling (adjust frequency)
        // Initial interval set here, logic inside fetchRagStatus adjusts frequency
        statusIntervalRef.current = setInterval(() => fetchRagStatus(false), 15000);

        // Setup WebSocket connection
        const newSocket = io(process.env.REACT_APP_API_URL || '', { path: '/socket.io' });
        setSocket(newSocket);

        newSocket.on('connect', () => {
            console.log('WebSocket connected');
            newSocket.emit('joinRoom', `app-${appId}`); // Join app-specific room
        });

        newSocket.on('ragStatusUpdate', (update) => {
            console.log('RAG Status Update Received:', update);
            setRagStatus(prevStatus => ({ ...prevStatus, ...update }));
             if (update.lastMessage) {
                setLastMessage(update.lastMessage);
            }
            // Refetch total file count if indexing finished, stopped, or errored
            if (update.status && (
                update.status === 'completed' || 
                update.status === 'stopped' || 
                update.status === 'error'
                )) {
                fetchTotalFiles(); 
            }
        });

        newSocket.on('ragProgress', (progressData) => {
            console.log('RAG Progress:', progressData);
            setLastMessage(progressData.message); // Update last message for progress bar
            // Optionally update parts of ragStatus if needed, e.g., percentage
            // setRagStatus(prevStatus => ({ ...prevStatus, progress: progressData.progress }));
        });

        newSocket.on('disconnect', () => {
            console.log('WebSocket disconnected');
        });

        newSocket.on('error', (error) => {
            console.error('WebSocket error:', error);
            // Handle WebSocket errors appropriately
        });

        return () => {
            console.log('Cleaning up WebSocket connection and interval');
            if (socket) {
                socket.close();
            }
            if (statusIntervalRef.current) {
                 clearInterval(statusIntervalRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, fetchRagStatus, fetchTotalFiles]); // fetchFiles/fetchRagStatus are stable due to useCallback

    // --- Action Handlers ---

    const handleNavigate = (item) => {
        if (item.type === 'folder') {
            const newPath = currentPath ? `${currentPath}/${item.name}` : item.name;
            fetchFiles(newPath);
        }
        // Potentially add download/view logic for files later
    };

    const handleNavigateUp = () => {
        if (!currentPath) return; // Already at root
        const pathParts = currentPath.split('/');
        const parentPath = pathParts.slice(0, -1).join('/');
        fetchFiles(parentPath);
    };

    const handleOpenCreateFolderDialog = () => {
        setNewFolderName('');
        setCreateFolderOpen(true);
    };

    const handleCloseCreateFolderDialog = () => {
        setCreateFolderOpen(false);
    };

    const handleCreateFolder = async () => {
        if (!newFolderName.trim()) {
            setSnackbar({ open: true, message: 'Folder name cannot be empty.', severity: 'warning' });
            return;
        }
        setLoading(true); // Indicate loading for file actions
        console.log(`[KnowledgeBasePage] Creating folder: ${newFolderName} at path: ${currentPath}`);
        try {
            // Use service hook or fetch directly
             await fetch(`/api/apps/${appId}/knowledge-base/folders?path=${encodeURIComponent(currentPath)}`, { 
                method: 'POST',
                headers: {
                     Authorization: `Bearer ${token}`,
                     'Content-Type': 'application/json'
                 },
                body: JSON.stringify({ folderName: newFolderName.trim() })
            });
            // Check response.ok if using fetch
            setSnackbar({ open: true, message: `Folder '${newFolderName.trim()}' created successfully.`, severity: 'success' });
            handleCloseCreateFolderDialog();
            fetchFiles(currentPath); // Refresh current directory
        } catch (err) { 
            console.error('[KnowledgeBasePage] Error creating folder:', err);
            const errorMsg = err.message || 'Failed to create folder.';
            setSnackbar({ open: true, message: errorMsg, severity: 'error' });
            setLoading(false); // Stop loading on error
        } 
        // Loading will be set to false by fetchFiles on success
    };

    const handleDelete = async (item) => {
        const itemPath = currentPath ? `${currentPath}/${item.name}` : item.name;
        const itemName = item.name;
        const itemType = item.type === 'folder' ? 'folder' : 'file';

        if (!window.confirm(`Are you sure you want to delete ${itemType} '${itemName}'?`)) {
            return;
        }
        
        setLoading(true);
        console.log(`[KnowledgeBasePage] Deleting item: ${itemName} at path: ${itemPath}`);
        try {
            // Use service hook or fetch directly
            await fetch(`/api/apps/${appId}/knowledge-base/files?path=${encodeURIComponent(itemPath)}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` },
            });
             // Check response.ok if using fetch
            setSnackbar({ open: true, message: `Item '${itemName}' deleted successfully.`, severity: 'success' });
            fetchFiles(currentPath); // Refresh the current directory
        } catch (err) {
            console.error('[KnowledgeBasePage] Error deleting item:', err);
            const errorMsg = err.message || 'Failed to delete item.';
            setSnackbar({ open: true, message: errorMsg, severity: 'error' });
            setLoading(false);
        }
    };

    const handleUploadClick = () => {
        // Trigger the hidden file input
        fileInputRef.current?.click();
    };

    const handleFileSelected = async (event) => {
        const selectedFiles = event.target.files;
        if (!selectedFiles || selectedFiles.length === 0) {
            return;
        }

        setLoading(true);
        console.log(`[KnowledgeBasePage] Starting upload of ${selectedFiles.length} file(s) to path: ${currentPath}`);

        const uploadPromises = Array.from(selectedFiles).map(file => {
            const formData = new FormData();
            formData.append('file', file);
            console.log(`[KnowledgeBasePage]   - Uploading: ${file.name}`);
            // Use fetch directly for multipart
            return fetch(`/api/apps/${appId}/knowledge-base/files?path=${encodeURIComponent(currentPath)}`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    // Content-Type is set automatically by browser for FormData
                },
                body: formData
            })
            .then(async response => { 
                if (!response.ok) { 
                    const errorData = await response.json().catch(() => ({})); // Catch JSON parse errors
                    return { status: 'rejected', fileName: file.name, error: new Error(errorData.message || `Upload failed with status ${response.status}`) };
                }
                return { status: 'fulfilled', fileName: file.name, response };
            })
            .catch(error => ({ status: 'rejected', fileName: file.name, error }));
        });

        const results = await Promise.allSettled(uploadPromises);

        // Reset input value after processing starts
        event.target.value = null;

        let successfulUploads = 0;
        let failedUploads = 0;
        const errorMessages = [];

        results.forEach(result => {
            if (result.status === 'fulfilled' && result.value.status === 'fulfilled') {
                successfulUploads++;
                console.log(`[KnowledgeBasePage]   - Success: ${result.value.fileName}`);
            } else {
                failedUploads++;
                const errorInfo = result.status === 'fulfilled' ? result.value.error : result.reason;
                const fileName = result.status === 'fulfilled' ? result.value.fileName : 'Unknown file';
                const errorMsg = errorInfo?.message || 'Unknown upload error';
                console.error(`[KnowledgeBasePage]   - Failed: ${fileName}`, errorInfo);
                errorMessages.push(`'${fileName}': ${errorMsg}`);
            }
        });

        let snackbarMessage = '';
        let snackbarSeverity = 'info';

        if (successfulUploads > 0 && failedUploads === 0) {
            snackbarMessage = `Successfully uploaded ${successfulUploads} file(s).`;
            snackbarSeverity = 'success';
        } else if (successfulUploads > 0 && failedUploads > 0) {
            snackbarMessage = `Uploaded ${successfulUploads} file(s). Failed to upload ${failedUploads} file(s). Errors: ${errorMessages.slice(0, 2).join('; ')}${errorMessages.length > 2 ? '...' : ''}`;
            snackbarSeverity = 'warning';
        } else if (successfulUploads === 0 && failedUploads > 0) {
            snackbarMessage = `Failed to upload ${failedUploads} file(s). Errors: ${errorMessages.slice(0, 2).join('; ')}${errorMessages.length > 2 ? '...' : ''}`;
            snackbarSeverity = 'error';
        }

        if (snackbarMessage) {
            setSnackbar({ open: true, message: snackbarMessage, severity: snackbarSeverity });
        }

        console.log(`[KnowledgeBasePage] Upload complete. Success: ${successfulUploads}, Failed: ${failedUploads}`);
        // If there were any uploads (successful or failed), refresh the directory.
        if (successfulUploads > 0 || failedUploads > 0) {
            fetchFiles(currentPath);
        } else {
            setLoading(false); // No uploads attempted or an issue before starting, stop loading
        }
    };

    // --- RAG Actions ---
    const handleStartIndexing = async () => {
        if (!appId || ragStatus.isIndexing || isActionLoading) return;
        if (!window.confirm("Start a full re-index? This will process all files and replace existing embeddings.")) return;

        console.log("[KnowledgeBasePage] Triggering full re-indexing...");
        setIsActionLoading(true);
        setRagStatus(prev => ({ ...prev, isIndexing: true, stopRequested: false, error: null, lastMessage: "Requesting full re-index..." }));

        try {
            const result = await startIndexing(appId);
            setSnackbar({ open: true, message: result.message || "Full re-index process initiated.", severity: 'info' });
            if (statusIntervalRef.current) clearInterval(statusIntervalRef.current);
            statusIntervalRef.current = setInterval(() => fetchRagStatus(false), 5000);
            fetchRagStatus(false);
        } catch (err) {
            console.error('[KnowledgeBasePage] Error starting full re-indexing:', err);
            const errorMsg = err.message || 'Failed to start full re-indexing.';
            setSnackbar({ open: true, message: errorMsg, severity: 'error' });
            setRagStatus(prev => ({ ...prev, isIndexing: false, error: errorMsg }));
            setIsActionLoading(false);
        }
    };

    const handleContinueIndexing = async () => {
        if (!appId || ragStatus.isIndexing || isActionLoading) return;
        if (!window.confirm("Continue indexing? This will process only new or previously unprocessed files.")) return;

        console.log("[KnowledgeBasePage] Triggering continue indexing...");
        setIsActionLoading(true);
        setRagStatus(prev => ({ ...prev, isIndexing: true, stopRequested: false, error: null, lastMessage: "Requesting continue indexing..." }));

        try {
            const result = await continueIndexing(appId);
            setSnackbar({ open: true, message: result.message || "Continue indexing process initiated.", severity: 'info' });
             if (statusIntervalRef.current) clearInterval(statusIntervalRef.current);
             statusIntervalRef.current = setInterval(() => fetchRagStatus(false), 5000);
             fetchRagStatus(false);
        } catch (err) {
            console.error('[KnowledgeBasePage] Error continuing indexing:', err);
            const errorMsg = err.message || 'Failed to start continue indexing.';
            setSnackbar({ open: true, message: errorMsg, severity: 'error' });
            setRagStatus(prev => ({ ...prev, isIndexing: false, error: errorMsg }));
             setIsActionLoading(false);
        }
    };

     const handleStopIndexing = async () => {
        if (!appId || !ragStatus.isIndexing || ragStatus.stopRequested || isActionLoading) return;
         if (!window.confirm("Are you sure you want to stop the current indexing process?")) return;

        console.log("[KnowledgeBasePage] Triggering stop indexing...");
        setIsActionLoading(true);
        setRagStatus(prev => ({ ...prev, stopRequested: true, lastMessage: "Requesting stop..." }));

        try {
            const result = await stopIndexing(appId);
            setSnackbar({ open: true, message: result.message || "Stop request sent.", severity: 'info' });
             fetchRagStatus(false);
        } catch (err) {
            console.error('[KnowledgeBasePage] Error stopping indexing:', err);
            const errorMsg = err.message || 'Failed to send stop request.';
            setSnackbar({ open: true, message: errorMsg, severity: 'error' });
            setRagStatus(prev => ({ ...prev, stopRequested: false, error: errorMsg }));
             setIsActionLoading(false);
        }
    };

    // --- Render ---

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbar({ ...snackbar, open: false });
    };

    const isBusy = loading || ragStatus.isIndexing || isActionLoading;

    // Helper functions to parse progress from status message
    // Prioritize overall file progress, then look for chunk progress
    const fileProgressRegex = /Processing file (\d+)\/(\d+)/; // For X/Y files
    const chunkProgressRegex = /Generating embeddings for chunks (\d+)-(\d+)\/(\d+)/; // For chunk batches

    function calculateProgress(message) {
        if (!message) return 0;

        // Check for overall file progress first
        const fileMatch = message.match(fileProgressRegex);
        if (fileMatch) {
            const currentFile = parseInt(fileMatch[1], 10);
            const totalFiles = parseInt(fileMatch[2], 10);
            // Calculate base progress from files
            const fileProgress = totalFiles > 0 ? ((currentFile - 1) / totalFiles) * 100 : 0;
            
            // Check for chunk progress within the *current* file to add granularity
            const chunkMatch = message.match(chunkProgressRegex);
            let chunkProgressWithinFile = 0;
            if (chunkMatch) {
                const currentChunkEnd = parseInt(chunkMatch[2], 10);
                const totalChunksInFile = parseInt(chunkMatch[3], 10);
                // Calculate percentage of chunks done *for this file*
                chunkProgressWithinFile = totalChunksInFile > 0 ? (currentChunkEnd / totalChunksInFile) * 100 : 0;
            }

            // Add a fraction of the chunk progress to the overall file progress
            // This assumes each file contributes roughly equally to the total time.
            // The fraction is (chunk % / total files)
            const granularity = totalFiles > 0 ? (chunkProgressWithinFile / totalFiles) : 0;
            
            // Ensure progress doesn't exceed 100 or go below 0
            return Math.max(0, Math.min(100, fileProgress + granularity));
        }

        // Fallbacks for other messages (less precise)
        if (message.toLowerCase().includes('indexing') || 
            message.toLowerCase().includes('processing') || 
            message.toLowerCase().includes('deleting') || 
            message.toLowerCase().includes('generating') || 
            message.toLowerCase().includes('inserting') || 
            message.toLowerCase().includes('cleaning') ||
            message.toLowerCase().includes('counting') ||
            message.toLowerCase().includes('listing') ||
            message.toLowerCase().includes('parsing') ||
            message.toLowerCase().includes('chunking')) {
            return 5; // Small indeterminate-like progress for non-file steps
        }
         if (message.toLowerCase().includes('finished')) {
            return 100;
        }
        return 0; // Default for idle or unknown states
    }

    function extractDisplayMessage(message) {
         if (!message) return null;
         // Example: could remove the `(of Z total)` part if too verbose
         // return message.replace(/ \(of \d+ total\)/, '');
         return message; // Show full message for now
    }

    const renderStatusDetails = () => {
        // Use isStatusLoading for the initial fetch only
        if (isStatusLoading && !ragStatus) { 
            return <CircularProgress size={20} />;
        }
        // If initial fetch failed, show that error
        if (!ragStatus && ragStatus.error) { 
            return <Alert severity="error">Error loading initial status: {ragStatus.error}</Alert>;
        }
        // If ragStatus hasn't loaded yet (after initial check), show placeholder or small loader
        if (!ragStatus) {
            return <Typography variant="caption">Loading status...</Typography>; // Or null/small loader
        }

        const progress = calculateProgress(lastMessage || ragStatus.lastMessage);
        const displayMessage = extractDisplayMessage(lastMessage || ragStatus.lastMessage);
        const isBusy = ragStatus.isIndexing || isActionLoading;
        const isIdle = !isBusy;

        return (
            <Box sx={{ mt: 1 }}> 
                {/* Progress Bar and Message (only when busy) */} 
                {isBusy && (
                    <Box sx={{ width: '100%', mb: 1 }}>
                        <LinearProgress variant={progress > 0 ? "determinate" : "indeterminate"} value={progress} />
                        {displayMessage && <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>{displayMessage}</Typography>}
                    </Box>
                )}

                {/* Action Loading/Error Messages */} 
                {isActionLoading && !ragStatus.isIndexing && <Typography sx={{ mb: 1 }}>Processing request...</Typography>}
                {ragStatus.error && <Alert severity="error" sx={{ mb: 1 }}>Error: {ragStatus.error}</Alert>}
                
                {/* RAG Error Message (only when idle) */} 
                {isIdle && ragStatus.error && (
                    <Alert severity="error" sx={{ mb: 1 }}>
                        Indexing Error: {ragStatus.error} 
                        {/* {ragStatus.lastMessage && `(Last Step: ${ragStatus.lastMessage})`} */} 
                    </Alert>
                )}
                
                {/* Idle Status Display */} 
                {isIdle && !ragStatus.error && ( // Don't show idle details if there was an error
                    <Box>
                         <Typography variant="body2" sx={{ mb: 0.5 }}> 
                            Status: <strong>{ragStatus.status || 'Idle'}</strong>
                            {ragStatus.indexingStartTime && !ragStatus.isIndexing && ` (Last run finished: ${new Date(ragStatus.indexingStartTime).toLocaleString()})`}
                        </Typography>
                        <Typography variant="body2">
                            Indexed <strong>{ragStatus.indexedDocuments ?? 0}</strong> Documents 
                            (Total Chunks: {ragStatus.totalChunks ?? 0})
                            {/* Display total file count if available */} 
                            {!isLoadingTotalFiles && totalFilesCount !== null && 
                                ` from ${totalFilesCount} Documents total in Storage`
                            }
                            {isLoadingTotalFiles && 
                                <Typography component="span" variant="caption"> (Loading total...)</Typography>
                            }
                            {errorTotalFiles && 
                                <Typography component="span" variant="caption" color="error"> (Error loading total file count)</Typography>
                            }
                        </Typography>
                    </Box>
                )}

                {/* Active Indexing Status */} 
                {isBusy && (
                     <Typography variant="body2">
                        Status: <strong>{ragStatus.status || (ragStatus.stopRequested ? 'Stopping...' : 'Indexing...')}</strong>
                        {ragStatus.indexingStartTime && ` (Started: ${new Date(ragStatus.indexingStartTime).toLocaleString()})`}
                    </Typography>
                )}
            </Box>
        );
    };

    return (
        <Box sx={{ height: 'calc(100vh - 64px - 48px)', display: 'flex', flexDirection: 'column' }}> 
            <Typography variant="h5" gutterBottom sx={{ mb: 1 }}>
                Knowledge Base
            </Typography>

            <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={1} alignItems="center" justifyContent="space-between">
                     <Grid item xs={12} sm={6} md={4}>
                          <Typography variant="h6" gutterBottom>RAG Indexing</Typography>
                     </Grid>
                     <Grid item xs={12} sm={6} md={8} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                          <Button
                              variant="contained"
                              color="warning"
                              startIcon={(ragStatus.isIndexing || isActionLoading) && !ragStatus.stopRequested ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
                              onClick={handleStartIndexing}
                              disabled={ragStatus.isIndexing || isActionLoading}
                              size="small"
                              title="Process all files, replacing existing embeddings."
                          >
                              Re-Index All
                          </Button>
                           <Button
                              variant="contained"
                              color="info"
                              startIcon={(ragStatus.isIndexing || isActionLoading) && !ragStatus.stopRequested ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
                              onClick={handleContinueIndexing}
                              disabled={ragStatus.isIndexing || isActionLoading}
                              size="small"
                              title="Process only new or previously unprocessed files."
                          >
                              Continue Indexing
                          </Button>
                           <Button
                              variant="contained"
                              color="error"
                              startIcon={ragStatus.stopRequested || (isActionLoading && ragStatus.isIndexing) ? <CircularProgress size={20} color="inherit" /> : <StopCircleIcon />}
                              onClick={handleStopIndexing}
                              disabled={!ragStatus.isIndexing || ragStatus.stopRequested || isActionLoading}
                              size="small"
                              title="Stop the current indexing process."
                          >
                              {ragStatus.stopRequested ? 'Stopping...' : 'Stop Indexing'}
                          </Button>
                          <IconButton onClick={() => fetchRagStatus(false)} disabled={isStatusLoading} size="small" title="Refresh Status">
                              <RefreshIcon fontSize="small" />
                          </IconButton>
                     </Grid>
                 </Grid>
                 {isStatusLoading && !ragStatus.isIndexing && <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb:1 }}>Loading status...</Typography>}
                 {renderStatusDetails()}
            </Paper>
            
             <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                 {currentPath && (
                     <Button 
                        variant="outlined" 
                        size="small"
                        startIcon={<ArrowUpwardIcon />} 
                        onClick={handleNavigateUp}
                        disabled={isBusy}
                    >
                         Up
                     </Button>
                 )}
                 <Typography variant="body1" sx={{ flexGrow: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                     Path: /{currentPath}
                 </Typography>
                <Button 
                    variant="contained" 
                    color="primary" 
                    startIcon={<CreateNewFolderIcon />} 
                    onClick={handleOpenCreateFolderDialog}
                    disabled={isBusy}
                    size="small"
                >
                    New Folder
                </Button>
                <Button 
                    variant="contained" 
                    color="secondary" 
                    startIcon={<UploadFileIcon />} 
                    onClick={handleUploadClick}
                    disabled={isBusy}
                    size="small"
                >
                    Upload Files
                </Button>
                <IconButton onClick={() => fetchFiles(currentPath)} disabled={isBusy} size="small" title="Refresh File List">
                    <RefreshIcon />
                </IconButton>
                 <input 
                    type="file" 
                    ref={fileInputRef} 
                    onChange={handleFileSelected}
                    style={{ display: 'none' }}
                    multiple
                 />
             </Box>

             {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

             <Box sx={{ flexGrow: 1, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                 {loading ? (
                     <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                         <CircularProgress />
                     </Box>
                 ) : files.length === 0 ? (
                     <Typography sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                         This folder is empty.
                     </Typography>
                 ) : (
                     <List dense disablePadding>
                         {files.map((item, index) => (
                             <React.Fragment key={item.name + index}>
                                 <ListItem 
                                    button={item.type === 'folder'} 
                                    onClick={() => handleNavigate(item)} 
                                    disabled={isBusy}
                                    secondaryAction={
                                        <Tooltip title="Delete">
                                            <IconButton edge="end" aria-label="delete" onClick={(e) => { e.stopPropagation(); handleDelete(item); }} size="small" disabled={isBusy}> 
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    }
                                 >
                                     <ListItemIcon sx={{ minWidth: 32 }}>
                                         {item.type === 'folder' ? <FolderIcon fontSize="small" /> : <InsertDriveFileIcon fontSize="small" />}
                                     </ListItemIcon>
                                     <ListItemText 
                                        primary={item.name} 
                                     />
                                 </ListItem>
                                 {index < files.length - 1 && <Divider component="li" />} 
                             </React.Fragment>
                         ))}
                     </List>
                 )}
             </Box>
            
            <Dialog open={createFolderOpen} onClose={handleCloseCreateFolderDialog}>
                 <DialogTitle>Create New Folder</DialogTitle>
                 <DialogContent>
                     <TextField
                         autoFocus
                         margin="dense"
                         id="name"
                         label="Folder Name"
                         type="text"
                         fullWidth
                         variant="standard"
                         value={newFolderName}
                         onChange={(e) => setNewFolderName(e.target.value)}
                         onKeyPress={(e) => { if (e.key === 'Enter') { handleCreateFolder(); } }}
                     />
                 </DialogContent>
                 <DialogActions>
                     <Button onClick={handleCloseCreateFolderDialog}>Cancel</Button>
                     <Button onClick={handleCreateFolder} disabled={!newFolderName.trim() || isBusy}>Create</Button>
                 </DialogActions>
             </Dialog>

             <Snackbar 
                open={snackbar.open} 
                autoHideDuration={6000} 
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default KnowledgeBasePage; 